import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Anchor } from 'lucide-react';

function validateRegistration(password, confirmPassword) {
  if (password !== confirmPassword) return 'Passwords do not match';
  if (password.length < 6) return 'Password must be at least 6 characters';
  return null;
}

export const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateRegistration(password, confirmPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    const result = await register(name, email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1632911943189-bdcecf8b1fb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjaGVmJTIwa2l0Y2hlbiUyMGJyaWdodHxlbnwwfHx8fDE3NzYyMzQ2OTN8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="h-full w-full bg-[#0A192F]/30 flex items-end p-12">
          <div className="text-white">
            <h2 className="font-heading text-4xl font-bold mb-4">Join the Club</h2>
            <p className="font-body text-lg opacity-90">Create an account to start managing your kitchen inventory with your household.</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <Anchor className="w-10 h-10 text-[#0A192F]" />
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#0A192F]">Le Boating Club</h1>
              <p className="text-xs text-[#475569] uppercase tracking-[0.2em]">Kitchen Inventory</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-[#0A192F] mb-2">Create Account</h2>
            <p className="text-[#475569] font-body">Sign up to get started with your kitchen inventory</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-6 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                className="border-[#E2E8F0] focus:border-[#0A192F] focus:ring-[#0A192F] h-12"
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="border-[#E2E8F0] focus:border-[#0A192F] focus:ring-[#0A192F] h-12"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="border-[#E2E8F0] focus:border-[#0A192F] focus:ring-[#0A192F] h-12"
                data-testid="password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider text-[#475569] font-bold">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="border-[#E2E8F0] focus:border-[#0A192F] focus:ring-[#0A192F] h-12"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0A192F] text-white hover:bg-[#0F294D] uppercase tracking-wide text-sm font-bold"
              data-testid="register-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E2E8F0]"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-xs uppercase tracking-wider text-[#475569]">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={loginWithGoogle}
            variant="outline"
            className="w-full h-12 border-[#E2E8F0] text-[#0A192F] hover:bg-slate-50 font-body"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="mt-8 text-center text-[#475569] font-body text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0A192F] font-bold hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
